import React, { useEffect, useRef, useState } from "react";
import { History, LayoutList, ChevronDown, Loader2 } from "lucide-react";
import { useConversationList } from "../hooks/useConversationList";
import { ConversationHistoryItem } from "./ConversationHistoryItem";

interface HistoryDropdownProps {
  currentConversationId: string | null;
  onSelectConversation: (id: string) => void;
}

export const HistoryDropdown: React.FC<HistoryDropdownProps> = ({
  currentConversationId,
  onSelectConversation,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { conversations, loading, hasMore, loadConversations } = useConversationList();
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (isOpen && !hasLoadedRef.current) {
      loadConversations();
      hasLoadedRef.current = true;
    }
  }, [isOpen, loadConversations]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (id: string) => {
    onSelectConversation(id);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-base-content/70 transition-colors hover:bg-base-200 active:bg-base-300"
        title="Historique des conversations"
      >
        <History className="size-5" />
        <ChevronDown className={`size-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-1 z-50 w-64 overflow-hidden rounded-xl border border-base-300 bg-base-100 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between border-b border-base-200 bg-base-50/50 px-4 py-2.5">
            <div className="flex items-center gap-2">
              <LayoutList className="size-4 text-primary" />
              <span className="text-xs font-semibold uppercase tracking-wider text-base-content/50">
                Historique
              </span>
            </div>
          </div>

          <div className="max-h-[320px] overflow-y-auto p-1.5">
            {loading && conversations.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="size-6 animate-spin text-primary/40" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center px-4">
                <p className="text-sm text-base-content/40">
                  Vous n'avez pas encore de conversation
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {conversations.map((conv) => (
                  <ConversationHistoryItem
                    key={conv.conversation_id}
                    conversation={conv}
                    isActive={conv.conversation_id === currentConversationId}
                    onSelect={handleSelect}
                  />
                ))}
                {hasMore && (
                  <button
                    onClick={() => loadConversations(true)}
                    disabled={loading}
                    className="mt-1 w-full rounded-lg py-2 text-xs font-medium text-primary hover:bg-primary/5 disabled:opacity-50"
                  >
                    {loading ? "Chargement..." : "Voir plus"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
